import {
  CanvasTexture,
  Color,
  ConeGeometry,
  CylinderGeometry,
  LinearFilter,
  LinearMipMapLinearFilter,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Sprite,
  SpriteMaterial,
  Vector3,
} from "three";
import { excludeSubtreeFromSelectAndAo } from "../engine/postprocesses/aoVisibility";
import { formatLength } from "../core/units/LengthUnit";

export class SimpleMeter extends Object3D {
  private _lineGroup: Object3D = new Object3D();
  private readonly _labelSprite: Sprite;
  private readonly _screenSize = 0.025;
  private readonly _minScale = 0.06;
  private readonly _maxScale = 0.25;
  // Line tube/cones use these as "base at zoom=1"; multiplied by clamped zoom.
  private readonly _tubeRadius = 0.0004;
  private readonly _capLength = 0.005;
  private readonly _capRadius = 0.002;
  private readonly _minLineScale = 0.7;
  private readonly _maxLineScale = 3;
  private _lineScale = 1;
  private _aspectRatio = 1;
  private _zoom = 0;
  private _size = 0;
  private _direction = new Vector3(1, 0, 0);

  get size() {
    return this._size;
  }

  get direction() {
    return this._direction;
  }

  private _attachTo?: Object3D;
  get attachTo() {
    return this._attachTo;
  }

  get labelSprite() {
    return this._labelSprite;
  }

  constructor(size: number, attachTo?: Object3D) {
    super();
    this._attachTo = attachTo;
    this._size = size;
    this.add(this._lineGroup);
    this._rebuildLine(size);

    this._labelSprite = createSprite(0);
    this.add(this._labelSprite);
    excludeSubtreeFromSelectAndAo(this);
  }

  private _disposeLine() {
    for (const child of [...this._lineGroup.children]) {
      this._lineGroup.remove(child);
      const mesh = child as Mesh;
      mesh.geometry?.dispose();
      (mesh.material as MeshBasicMaterial)?.dispose?.();
    }
  }

  private _rebuildLine(size: number) {
    this._disposeLine();
    const mat = new MeshBasicMaterial({ color: 0x000000 });

    // Tube spans the full length; cones overlay the endpoints.
    const tube = new Mesh(
      new CylinderGeometry(this._tubeRadius, this._tubeRadius, size, 12),
      mat,
    );
    tube.rotation.x = Math.PI / 2;
    tube.position.z = size / 2;
    tube.userData.role = "tube";
    this._lineGroup.add(tube);

    // Cone end caps with tips at the endpoints, pointing outward.
    const capStart = new Mesh(
      new ConeGeometry(this._capRadius, this._capLength, 16),
      mat,
    );
    capStart.rotation.x = -Math.PI / 2;
    capStart.userData.role = "capStart";
    this._lineGroup.add(capStart);

    const capEnd = new Mesh(
      new ConeGeometry(this._capRadius, this._capLength, 16),
      mat,
    );
    capEnd.rotation.x = Math.PI / 2;
    capEnd.userData.role = "capEnd";
    this._lineGroup.add(capEnd);

    this._applyLineScale();
    excludeSubtreeFromSelectAndAo(this._lineGroup);
  }

  private _applyLineScale() {
    const s = this._lineScale;
    for (const child of this._lineGroup.children) {
      const role = child.userData.role;
      if (role === "tube") {
        child.scale.x = s;
        child.scale.z = s;
        // Y is the cylinder's length axis; leave at 1 so length matches _size.
        child.scale.y = 1;
      } else if (role === "capStart") {
        child.scale.setScalar(s);
        // Tip sits past the endpoint (outside the line).
        child.position.z = -(this._capLength / 2) * s;
      } else if (role === "capEnd") {
        child.scale.setScalar(s);
        child.position.z = this._size + (this._capLength / 2) * s;
      }
    }
  }

  private _applySpriteScale() {
    const base = Math.max(
      this._minScale,
      Math.min(this._maxScale, this._zoom * this._screenSize),
    );
    this._labelSprite.scale.set(base * this._aspectRatio, base, 1);
  }

  setSizeLabel(size: number) {
    this._labelSprite.material?.map?.dispose();
    const { data, width, height } = createTexture(formatLength(size));
    this._aspectRatio = width / height;
    this._labelSprite.material.map = data;
    this._applySpriteScale();
  }

  setSize(size: number) {
    this._size = size;
    this._rebuildLine(size);

    this._labelSprite.material?.map?.dispose();
    const { data, width, height } = createTexture(formatLength(size));

    this._aspectRatio = width / height;

    this._labelSprite.material.map = data;
    this._labelSprite.position.setZ(size * 0.5);
    this._applySpriteScale();
  }

  setZoom(zoom: number) {
    this._zoom = zoom;
    this._lineScale = Math.max(
      this._minLineScale,
      Math.min(this._maxLineScale, zoom),
    );
    this._applySpriteScale();
    this._applyLineScale();
  }

  setDirection(direction: Vector3, up: Vector3 = new Vector3(0, 1, 0)) {
    this._direction = direction.clone().normalize();
    const z = direction.clone().normalize();
    const x = new Vector3().crossVectors(up, z).normalize();
    const y = new Vector3().crossVectors(z, x).normalize();
    const m = new Matrix4().makeBasis(x, y, z);
    this.quaternion.setFromRotationMatrix(m);
  }

  updateScale(zoom: number) {
    //this._labelSprite.scale.set(1, 1, zoom);
  }

  dispose() {
    this._labelSprite.material?.map?.dispose();
    this._disposeLine();
  }
}

export const createSprite = (
  lenght: number,
  _unit = "cm",
  upVector = new Vector3(0, 1, 0),
  overridenLength?: number,
) => {
  const pos = upVector.clone().multiplyScalar(0.5 * lenght);
  const len = overridenLength ? overridenLength : lenght;
  const text = formatLength(len);
  return createSpriteMeasure(text, pos, 0);
};

export const createSpriteMeasure = (
  text: string,
  pos: Vector3,
  angle: number,
) => {
  const { data, width, height } = createTexture(text, 0, 0, 10, 10);
  const spriteMaterial = new SpriteMaterial({
    color: new Color(0xffffff),
    transparent: true,
    depthWrite: false,
    depthTest: false,
    map: data,
  });

  const spriteObject = new Sprite();

  spriteObject.scale.set((1 * width) / height, 1, 1);
  spriteObject.position.set(pos.x, pos.y, pos.z);

  spriteObject.name = "Sprite";
  spriteObject.renderOrder = -999;
  spriteObject.material = spriteMaterial;
  excludeSubtreeFromSelectAndAo(spriteObject);
  return spriteObject;
};

export const createTexture = (text: string, x = 0, y = 0, h = 10, s = 10) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d", { alpha: true })!;

  const hp = h * 0.2 * s;
  const wp = h * 0.2 * s;

  const tmpCanvas = document.createElement("canvas");
  const tmpCtx = tmpCanvas.getContext("2d")!;
  const hf = h * s * 0.7;
  tmpCtx.font = `${hf}px Arial`;
  const a = tmpCtx.measureText(text);
  const textWidth = a.width;
  const textHeight = a.actualBoundingBoxAscent + a.actualBoundingBoxDescent;

  const w1 = textWidth + wp * 2;
  const h1 = textHeight + hp * 2;

  const w2 = Math.max(Math.pow(2, Math.ceil(Math.log2(w1))), 16);
  const h2 = Math.max(Math.pow(2, Math.ceil(Math.log2(h1))), 16);

  canvas.width = w2;
  canvas.height = h2;

  ctx.fillStyle = "#000000FF";
  const arc = Math.min(w1, h1) / 4;
  ctx.beginPath();
  (ctx as any).roundRect((w2 - w1) / 2, (h2 - h1) / 2, w1, h1, arc);
  ctx.fill();

  ctx.font = `${hf}px Arial`;
  ctx.fillStyle = "#FFFFFFFF";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const textX = w2 / 2;
  const textY = h2 / 2;
  ctx.fillText(text, textX, textY);

  const texture = new CanvasTexture(canvas);
  texture.minFilter = LinearMipMapLinearFilter;
  texture.magFilter = LinearFilter;
  texture.anisotropy = 2;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;

  return {
    data: texture,
    width: w2,
    height: h2,
  };
};
